import { useState } from "react";
import {
  formatQty,
  MATERIAL_ORDER_STATUS_LABELS,
  MATERIAL_ORDER_STATUSES,
  MATERIAL_SOURCE_LABELS,
  MATERIAL_SOURCES,
  type MaterialOrderStatus,
  type MaterialSource,
  type TaskDto,
  type TaskMaterialDto,
} from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { IconPlus, IconTrash } from "../../app/icons";
import { api } from "../../lib/api";
import { can, hasModule, useMe } from "../../lib/auth";
import { useToast } from "../../lib/toast";
import { parseQty } from "./MaterialDialogs";
import { errMsg, useMaterials } from "./useMaterials";

/** Regel in de taakdialoog; `id` komt van de client. */
export interface DraftLine {
  id: string;
  materialId: string;
  quantity: string;
  source: MaterialSource;
}

export const draftOf = (t: TaskDto | undefined): DraftLine[] =>
  (t?.materials ?? [])
    .filter((l) => !l.bookedAt)
    .map((l) => ({ id: l.id, materialId: l.materialId, quantity: formatQty(l.quantity), source: l.source }));

/** Materialen van een taak opslaan (na het opslaan van de taak zelf). Afgeboekte regels blijven staan. */
export const saveTaskMaterials = (taskId: string, lines: DraftLine[]) =>
  api(`/tasks/${taskId}/materials`, {
    method: "PUT",
    body: {
      lines: lines
        .filter((l) => l.materialId && parseQty(l.quantity))
        .map((l) => ({ id: l.id, materialId: l.materialId, quantity: parseQty(l.quantity), source: l.source })),
    },
  });

const unbooked = (t: TaskDto) => t.materials.filter((l) => !l.bookedAt);
/** Moet er bij Klaar nog worden afgeboekt? */
export const needsBooking = (t: TaskDto) => unbooked(t).length > 0;

/** Bewerken in de taakdialoog: artikel, aantal en uit voorraad of bestellen. */
export function TaskMaterialsEditor({
  buildingId,
  task,
  lines,
  onChange,
}: {
  buildingId: string;
  task?: TaskDto;
  lines: DraftLine[];
  onChange: (lines: DraftLine[]) => void;
}) {
  const { data: me } = useMe();
  const toast = useToast();
  const catalog = useMaterials(buildingId);
  const [orders, setOrders] = useState<Record<string, MaterialOrderStatus>>({});
  if (!hasModule(me, "materials", "edit")) return null;
  const options = (catalog.data ?? []).filter((m) => !m.archived);
  const booked = (task?.materials ?? []).filter((l) => l.bookedAt);
  const saved = (id: string) => task?.materials.find((l) => l.id === id);
  const set = (i: number, patch: Partial<DraftLine>) =>
    onChange(lines.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  async function order(l: TaskMaterialDto, status: MaterialOrderStatus) {
    setOrders((o) => ({ ...o, [l.id]: status }));
    try {
      await api(`/task-materials/${l.id}`, { method: "PATCH", body: { orderStatus: status } });
    } catch (e) {
      toast(errMsg(e));
    }
  }

  return (
    <fieldset className="q mat-task">
      <legend className="ql">Materialen</legend>
      {lines.map((l, i) => {
        const m = options.find((x) => x.id === l.materialId);
        const s = saved(l.id);
        return (
          <div className="mat-line" key={l.id}>
            <select aria-label="Artikel" value={l.materialId} onChange={(e) => set(i, { materialId: e.target.value })}>
              <option value="">Kies een artikel…</option>
              {options.map((x) => (
                <option key={x.id} value={x.id}>
                  {x.name}
                  {x.articleNo ? ` (${x.articleNo})` : ""}
                </option>
              ))}
            </select>
            <input
              aria-label="Aantal"
              className="num"
              inputMode="decimal"
              value={l.quantity}
              onChange={(e) => set(i, { quantity: e.target.value })}
            />
            <select
              aria-label="Uit voorraad of bestellen"
              value={l.source}
              onChange={(e) => set(i, { source: e.target.value as MaterialSource })}
            >
              {MATERIAL_SOURCES.map((x) => (
                <option key={x} value={x}>
                  {MATERIAL_SOURCE_LABELS[x]}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn ghost small ico"
              aria-label="Regel verwijderen"
              onClick={() => onChange(lines.filter((_, j) => j !== i))}
            >
              <IconTrash size={18} aria-hidden="true" />
            </button>
            <span className="mat-meta muted small">
              {l.source === "stock" && m && `Voorraad ${formatQty(m.stock?.quantity ?? 0)} ${m.unit}`}
              {l.source === "stock" && m?.stock?.location && ` · ${m.stock.location}`}
              {l.source === "order" && m?.supplier && `Leverancier ${m.supplier.name}`}
            </span>
            {l.source === "order" && s?.source === "order" && can(me, "material.order") && (
              <select
                aria-label="Bestelstatus"
                className="mat-order"
                value={orders[s.id] ?? s.orderStatus ?? "to_order"}
                onChange={(e) => void order(s, e.target.value as MaterialOrderStatus)}
              >
                {MATERIAL_ORDER_STATUSES.map((x) => (
                  <option key={x} value={x}>
                    {MATERIAL_ORDER_STATUS_LABELS[x]}
                  </option>
                ))}
              </select>
            )}
          </div>
        );
      })}
      <button
        type="button"
        className="btn small ghost"
        disabled={!options.length}
        onClick={() =>
          onChange([...lines, { id: crypto.randomUUID(), materialId: "", quantity: "1", source: "stock" }])
        }
      >
        <IconPlus size={18} aria-hidden="true" /> Materiaal
      </button>
      {!options.length && catalog.data && (
        <span className="hint">Nog geen artikelen voor dit gebouw. Voeg ze toe onder Materialen.</span>
      )}
      {booked.length > 0 && (
        <p className="hint">
          Afgeboekt: {booked.map((l) => `${formatQty(l.usedQuantity ?? 0)} ${l.unit} ${l.name}`).join(", ")}
        </p>
      )}
    </fieldset>
  );
}

/** Op de taakkaart: wat er mee moet, met voorraad, plek en bestelstatus. */
export function TaskMaterialsList({ task, onBook }: { task: TaskDto; onBook?: () => void }) {
  const { data: me } = useMe();
  if (!task.materials.length || !hasModule(me, "materials")) return null;
  const open = unbooked(task);
  return (
    <div className="mat-tlist">
      <span className="label">Materialen</span>
      <ul>
        {task.materials.map((l) => (
          <li key={l.id}>
            <span className="num">
              {formatQty(l.bookedAt ? (l.usedQuantity ?? 0) : l.quantity)} {l.unit}
            </span>{" "}
            <span className="grow">
              {l.name}
              <span className="muted small block">
                {l.bookedAt
                  ? "Afgeboekt"
                  : l.source === "order"
                    ? MATERIAL_ORDER_STATUS_LABELS[l.orderStatus ?? "to_order"]
                    : `Uit voorraad (${formatQty(l.stockQuantity ?? 0)})${l.location ? ` · ${l.location}` : ""}`}
              </span>
            </span>
          </li>
        ))}
      </ul>
      {open.length > 0 && task.status === "klaar" && onBook && (
        <button type="button" className="btn small" onClick={onBook}>
          Materialen afboeken
        </button>
      )}
    </div>
  );
}

/**
 * Afboeken bij afronden: per regel het werkelijk gebruikte aantal (voorgevuld). Uit voorraad gaat van de voorraad
 * in het gebouw af. Met `finish` zet *Afboeken en afronden* de taak daarna op Klaar.
 */
export function BookDialog({
  task,
  finish,
  onClose,
  onDone,
}: {
  task: TaskDto;
  /** Taak daarna op Klaar zetten (afboeken gestart vanuit de status). */
  finish?: () => Promise<void>;
  onClose: () => void;
  onDone: () => Promise<unknown>;
}) {
  const toast = useToast();
  const lines = unbooked(task);
  const [used, setUsed] = useState<Record<string, string>>(() =>
    Object.fromEntries(lines.map((l) => [l.id, formatQty(l.quantity)])),
  );
  const [busy, setBusy] = useState(false);
  const bad = lines.some((l) => parseQty(used[l.id] ?? "") === null);

  async function run(book: boolean) {
    setBusy(true);
    try {
      if (book)
        await api(`/tasks/${task.id}/materials/book`, {
          method: "POST",
          body: { lines: lines.map((l) => ({ id: l.id, usedQuantity: parseQty(used[l.id]!) })) },
        });
      if (finish) await finish();
      await onDone();
      if (book) toast("Materialen afgeboekt");
      onClose();
    } catch (e) {
      toast(errMsg(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`Materialen afboeken · ${task.code}`}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          {finish && (
            <button type="button" className="btn ghost" onClick={() => void run(false)} disabled={busy}>
              Later afboeken
            </button>
          )}
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="button" className="btn primary" onClick={() => void run(true)} disabled={busy || bad}>
            {finish ? "Afboeken en afronden" : "Afboeken"}
          </button>
        </>
      }
    >
      <p className="muted">Vul in hoeveel je echt gebruikt hebt. Uit voorraad gaat van de voorraad in het gebouw af.</p>
      {lines.map((l) => (
        <label className="field mat-book" key={l.id}>
          <span>
            {l.name}{" "}
            <span className="muted small">
              {l.source === "stock"
                ? `uit voorraad (${formatQty(l.stockQuantity ?? 0)} ${l.unit})`
                : MATERIAL_SOURCE_LABELS.order.toLowerCase()}
            </span>
          </span>
          <span className="mat-bookin">
            <input
              className="num"
              inputMode="decimal"
              value={used[l.id] ?? ""}
              aria-invalid={parseQty(used[l.id] ?? "") === null || undefined}
              onChange={(e) => setUsed((u) => ({ ...u, [l.id]: e.target.value }))}
            />
            <span className="muted">{l.unit}</span>
          </span>
        </label>
      ))}
    </Modal>
  );
}
